import { createContext, type ReactNode, useContext } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

/**
 * What a field held before the version being viewed changed it, keyed by the
 * field's API name. Supplied by the entity-version picker; absent everywhere
 * else, which is why every consumer of {@link DetailRow} keeps working
 * untouched.
 */
export interface PreviousValue {
  value: unknown;
  /** Resolved display name when the value is a foreign key. */
  label?: string | null;
}

const PreviousValuesContext = createContext<Readonly<Record<string, PreviousValue>> | null>(null);

/**
 * Show what each field used to be, struck through, on the rows a version
 * changed.
 *
 * Through context rather than a prop drilled to every row: the detail screens
 * render dozens of rows across several sections, and threading the map through
 * each one would put versioning knowledge in places that have no other reason
 * to know about it. A row opts in simply by declaring its `field`.
 */
export function PreviousValues({
  values,
  children,
}: {
  values: Readonly<Record<string, PreviousValue>> | null;
  children: ReactNode;
}) {
  return <PreviousValuesContext.Provider value={values}>{children}</PreviousValuesContext.Provider>;
}

/**
 * Whether this field is one the viewed version changed.
 *
 * Exported so a row can TINT ITSELF. The struck-through value alone is easy to
 * miss on a screen of forty fields — you have to already be looking at the row
 * to notice it — so the changed rows carry a background that finds the eye
 * first and the strikethrough explains once it lands.
 */
export function useFieldChanged(field?: string): boolean {
  const previous = useContext(PreviousValuesContext);
  return field != null && previous?.[field] != null;
}

/**
 * How a changed field is marked, in two parts.
 *
 * The RULE sits on the label, where the row begins, so a column of forty
 * fields can be scanned down its left edge — a 2px hard edge finds the eye far
 * better than a fill does. The FILL hugs the value text itself rather than the
 * cell, so it reads as a highlighter over the thing that changed instead of a
 * band across empty space; a cell-wide fill stretched to the column's full
 * width and made short values look like long ones.
 *
 * Amber in both, tying them to the history banner so "this is about versions"
 * reads as one idea rather than two unrelated highlights.
 */
export const CHANGED_RULE_CLASS = 'border-l-2 border-amber-400 dark:border-amber-500/70';
export const CHANGED_VALUE_CLASS =
  'inline-block rounded bg-amber-100/70 px-1.5 py-0.5 dark:bg-amber-950/40';

/**
 * One label/value pair inside a `<dl>` detail grid: a muted uppercase `<dt>`
 * label and a `<dd>` value. The enclosing `<dl>` owns its column layout (the
 * customer/SOW detail screens use a 4-column grid, the view sheets use 2), so
 * only the row markup is shared here. Used by the customer detail page, the SOW
 * detail page, and the team/contact view sheets.
 *
 * `field` is the row's API field name. It does nothing on its own; inside a
 * {@link PreviousValues} it makes the row show the value this version replaced.
 */
export function DetailRow({
  label,
  field,
  children,
}: {
  label: string;
  field?: string;
  children: ReactNode;
}) {
  const changed = useFieldChanged(field);
  return (
    <>
      {/* The label keeps its stripe either way — the rule is added to it, not
          swapped for it, so the grid's rhythm survives. */}
      <dt
        className={cn(
          // `border-transparent` is load-bearing: the rule is 2px of border
          // OUTSIDE the padding box, so adding it only on changed rows pushed
          // their labels 2px right of every other label in the column. Every
          // label reserves the space; only the colour changes.
          'border-l-2 border-transparent bg-muted/30 px-3 py-2 text-xs font-medium uppercase tracking-wide text-muted-foreground',
          changed && CHANGED_RULE_CLASS,
        )}
      >
        {label}
      </dt>
      <dd className="px-3 py-2 text-sm">
        <FieldValue field={field}>{children}</FieldValue>
      </dd>
    </>
  );
}

/**
 * A row's value, highlighted when the viewed version changed it.
 *
 * Both detail grids render this rather than assembling the mark and the
 * highlight themselves — the customer page and the employee page have separate
 * `DetailRow` implementations, and this is the one place that decides what a
 * changed value looks like.
 */
export function FieldValue({ field, children }: { field?: string; children: ReactNode }) {
  const changed = useFieldChanged(field);
  const content = (
    <>
      <PreviousValueMark field={field} />
      {children}
    </>
  );
  // A DIV, not a span, even though it renders inline-block. Some rows hand us
  // block markup — `AddressLines` emits one <div> per address line — and a
  // <span> wrapping a <div> is invalid nesting. `inline-block` is what makes
  // the chip shrink to its content; the element only has to be one that may
  // legally contain a block, and a <div> inside the <dd> is.
  return changed ? <div className={CHANGED_VALUE_CLASS}>{content}</div> : content;
}

/**
 * The struck-through prior value for one field, or nothing.
 *
 * Exported because the employee detail page has its own `DetailRow` with a
 * different grid; both render this so the two screens can't drift on what a
 * changed field looks like.
 */
export function PreviousValueMark({ field }: { field?: string }) {
  const previous = useContext(PreviousValuesContext);
  const changed = field ? previous?.[field] : undefined;
  if (!changed) return null;
  return (
    <span className="mr-2 text-muted-foreground/70 line-through decoration-muted-foreground/40">
      {formatPrevious(changed)}
    </span>
  );
}

/**
 * The struck-through value, rendered as plain text.
 *
 * Deliberately not the row's own renderer: re-running a badge, an address
 * block or a link over a historical value would produce a second live-looking
 * control beside the current one. Struck-through text reads unambiguously as
 * "this is what it was".
 */
function formatPrevious({ value, label }: PreviousValue): string {
  if (label != null) return label;
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return objectSummary(value);
  return String(value);
}

/**
 * Address-shaped jsonb as one line: "500 Main St, Austin, TX, 78701".
 *
 * The only object-valued columns wired to a detail row are the two customer
 * address fields, and `JSON.stringify` rendered those as a debug dump beside
 * the formatted current address. Joining the non-empty leaf values degrades
 * sensibly for any other object that reaches here.
 */
function objectSummary(value: object): string {
  const parts = Object.values(value as Record<string, unknown>).filter(
    (v): v is string | number =>
      (typeof v === 'string' && v.trim() !== '') || typeof v === 'number',
  );
  return parts.length > 0 ? parts.join(', ') : '—';
}

export interface DetailListRow {
  label: string;
  value: ReactNode;
}

/**
 * Convenience wrapper around {@link DetailRow} for the common 4-column detail
 * card (label / value, label / value). Screens whose column layout differs
 * (the 2-column view sheets) keep building their own `<dl>` from `DetailRow`.
 * Used by the activities and departments detail pages.
 */
export function DetailList({ rows }: { rows: DetailListRow[] }) {
  return (
    <dl className="grid grid-cols-1 sm:grid-cols-[160px_1fr_160px_1fr]">
      {rows.map((r) => (
        <DetailRow key={r.label} label={r.label}>
          {r.value}
        </DetailRow>
      ))}
    </dl>
  );
}

/**
 * Loading placeholder for a {@link DetailList} card. Pass the exact row count
 * the loaded card will render so the layout doesn't jump when data arrives.
 */
export function DetailListSkeleton({ rows }: { rows: number }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr_160px_1fr]">
      {Array.from({ length: rows }).map((_, i) => (
        <DetailRow key={`detail-skel-${i}`} label="">
          <Skeleton className="h-4 w-40" />
        </DetailRow>
      ))}
    </div>
  );
}

export function Muted({ children }: { children: ReactNode }) {
  return <span className="text-muted-foreground">{children}</span>;
}
